// AiBotiKarta — Nastavení → AI boti (FW-BOTI, etapa 1, 22. 10. 2026). Jen owner.
//
// Owner tu vydává tokeny konkrétním lidem pro schválený AI nástroj, hlídá platnost (90 dní),
// prodlužuje jedním klikem, ruší a zapíná zdravotní údaje. Admin token „Admin Aneta“ (180 dní) má vlastní
// tlačítko Vydat admin token. Každý token má jedno tlačítko Spravovat token (okno s akcemi). Token se ukáže jen jednou při vydání,
// v databázi je jen jeho otisk. Seznam povolených AI nástrojů je dole.
import React, { useRef, useState } from "react";
import { Bot, KeyRound, ShieldCheck, Plus, Copy, X as XIcon, RotateCcw, ChevronDown, Eye, Pencil, HeartPulse } from "lucide-react";
import Card from "./Card.jsx";
import { Modal, Field, Zapinatko, ErrorNote, SkeletonRows, Stitek } from "./ui.jsx";
import { useApiData, apiFetch } from "../lib/api.js";
import { ton } from "../lib/tony.js";
import { toast } from "./Toast.jsx";
import { confirmDialog } from "./confirmHost.jsx";
import { briefProBota } from "../lib/botBrief.js";

const ROLE = { owner: "Owner", manager: "Manažer", csm: "CSM", sales: "Sales", trener: "Trenér", bc_trener: "BC trenér", nutritionist: "Nutri" };
const datum = (d) => { if (!d) return ""; const x = new Date(d); return `${x.getDate()}. ${x.getMonth() + 1}. ${x.getFullYear()}`; };
const dniDo = (d) => Math.ceil((new Date(d).getTime() - Date.now()) / 86400000);
function kdy(d) {
  if (!d) return "zatím nepoužit";
  const x = new Date(d); const dnes = new Date();
  if (x.toDateString() === dnes.toDateString()) return `naposledy dnes ${x.getHours()}:${String(x.getMinutes()).padStart(2, "0")}`;
  return `naposledy ${datum(d)}`;
}
const ZAPIS_STITEK = {
  ukoly: "úkoly", meetingy: "meetingy", rutiny: "rutiny", fakturace: "fakturace", absence: "absence", upominky: "upomínky", skoleni: "školení",
  bonusy: "bonusy", vyplaty: "výplaty", ukoly_trenery: "úkoly trenérů", hlaseni: "hlášení", fond_hodin: "fond hodin", cashflow_model: "model cashflow",
};
const dnyText = (n) => `${n} ${n === 1 ? "den" : n < 5 ? "dny" : "dní"}`;
async function kopirujBrief(t) {
  try { await navigator.clipboard.writeText(briefProBota({ jmeno: t.user_name || t.user_email, role: t.user_role, zdravotni: t.zdravotni })); toast("Brief pro bota zkopírovaný", "success"); }
  catch { toast("Kopírování se nepovedlo", "error"); }
}
const inicialy = (s) => String(s || "?").split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((x) => x[0].toUpperCase()).join("");

async function posli(telo) {
  const r = await apiFetch("/api/admin-boti", { method: "POST", body: JSON.stringify(telo) });
  const j = await r.json().catch(() => ({}));
  if (!r.ok || !j.ok) throw new Error(j.error || `HTTP ${r.status}`);
  return j;
}

export default function AiBotiKarta() {
  const { data, loading, error, refetch } = useApiData("/api/admin-boti", { bezKese: true });
  const [vydani, setVydani] = useState(false);
  const [vydaniAdmin, setVydaniAdmin] = useState(false);
  const [sprava, setSprava] = useState(null); // id tokenu v okně Spravovat token
  const [nastrojEdit, setNastrojEdit] = useState(null); // null | {} | nástroj
  const [prace, setPrace] = useState("");

  if (loading && !data) return <Card title="AI boti týmu" ikona={Bot}><SkeletonRows rows={4} /></Card>;
  if (error && !data) return <ErrorNote onRetry={refetch}>AI boty se nepodařilo načíst.</ErrorNote>;

  const tokeny = data?.tokeny || [];
  const aktivni = tokeny.filter((t) => !t.zruseno_at);
  const zrusene = tokeny.filter((t) => t.zruseno_at);
  const nastroje = data?.nastroje || [];
  const maAdmin = aktivni.some((t) => t.typ === "admin");
  const spravovany = sprava ? aktivni.find((t) => t.id === sprava) : null;

  const akce = async (klic, telo, hlaska) => {
    setPrace(klic);
    try { await posli(telo); if (hlaska) toast(hlaska, "success"); refetch(); }
    catch (e) { toast(e.message, "error"); }
    finally { setPrace(""); }
  };

  const zrusit = async (t) => {
    if (!await confirmDialog(`Zrušit token AI bota pro ${t.typ === "admin" ? t.stitek : (t.user_name || t.user_email)}? Bot přestane okamžitě fungovat a token už nepůjde obnovit.`)) return;
    setSprava(null);
    akce(`z${t.id}`, { akce: "zrusit", id: t.id }, "Token je zrušený");
  };
  const zdravotni = async (t) => {
    const zapnout = !t.zdravotni;
    if (zapnout && !await confirmDialog({ title: "Zapnout zdravotní údaje?", description: `Bot ${t.user_name || t.user_email} uvidí zdravotní údaje klientek. Každé čtení se zapíše do auditu zdravotních dat.`, confirmLabel: "Zapnout", variant: "warning" })) return;
    akce(`d${t.id}`, { akce: "zdravotni", id: t.id, hodnota: zapnout }, zapnout ? "Zdravotní údaje zapnuté" : "Zdravotní údaje vypnuté");
  };
  const vypnoutSpolecny = async () => {
    if (!await confirmDialog("Vypnout společný token původního asistenta? Všichni, kdo ho používají, ztratí přístup. Vrátit to jde jen novým nastavením přes skript.")) return;
    akce("spol", { akce: "spolecny_vypnout" }, "Společný token je vypnutý");
  };

  return (
    <div className="space-y-4">
      <Card
        title="AI boti týmu"
        ikona={Bot}
        odznak={`${aktivni.length} ${aktivni.length === 1 ? "aktivní" : "aktivních"}`}
        actionPlna
        action={(
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
            <button type="button" className="btn-primary w-full sm:w-auto" onClick={() => setVydani(true)} disabled={!nastroje.some((n) => n.aktivni)}><Plus className="h-5 w-5" /> Vydat token</button>
            <button type="button" className="btn-secondary w-full sm:w-auto" onClick={() => setVydaniAdmin(true)} disabled={maAdmin || !nastroje.some((n) => n.aktivni)} title={maAdmin ? "Admin token už existuje, nejdřív ho zruš" : undefined}><ShieldCheck className="h-5 w-5" /> Vydat admin token</button>
          </div>
        )}
      >
        <p className="mb-3 text-sm text-slate-600">Každý bot vidí a smí jen to, co jeho člověk v adminu. Token platí {data?.platnost_dni || 90} dní, {data?.upozornit_dni || 14} dní před koncem ti přijde upozornění do zvonečku. Admin token platí {data?.admin_platnost_dni || 180} dní, upozornění přijde {data?.admin_upozornit_dni || 10} dní před koncem.</p>
        {!nastroje.some((n) => n.aktivni) && <p className={`mb-3 rounded-xl px-3 py-2 text-sm ${ton("pozor").pole}`}>Nejdřív přidej dole aspoň jeden povolený AI nástroj, token jde vydat jen pro něj.</p>}
        {aktivni.length === 0 && <p className="text-sm italic text-slate-500">Zatím žádný vydaný token.</p>}
        <div className="space-y-2">
          {aktivni.map((t) => {
            const zbyva = dniDo(t.platnost_do);
            const prosly = zbyva <= 0;
            const admin = t.typ === "admin";
            const brzy = !prosly && zbyva <= (admin ? (data?.admin_upozornit_dni || 10) : (data?.upozornit_dni || 14));
            const neaktivni = !t.nastroj_aktivni;
            const pozadi = prosly ? ton("problem").pole : brzy ? ton("pozor").pole : "bg-white";
            return (
              <div key={t.id} className={`flex flex-col gap-3 rounded-2xl border border-slate-200 p-3 sm:flex-row sm:items-start ${pozadi}`}>
                <div className="flex min-w-0 flex-1 gap-3">
                  <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-bold text-white ${ton("znacka").plny}`}>{admin ? <ShieldCheck className="h-5 w-5" /> : inicialy(t.user_name || t.user_email)}</div>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-slate-900">{admin ? t.stitek : (t.user_name || t.user_email)}</div>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {admin ? <Stitek ton="znacka" ikona={ShieldCheck}>Admin</Stitek> : <Stitek ton="znacka">{ROLE[t.user_role] || t.user_role || "bez role"}</Stitek>}
                      <Stitek ton="znacka" ikona={Bot}>{t.nastroj}</Stitek>
                      {neaktivni ? <Stitek ton="problem">nástroj vyřazený, token neplatí</Stitek>
                        : prosly ? <Stitek ton="problem">prošlý {datum(t.platnost_do)}, bot nefunguje</Stitek>
                        : brzy ? <Stitek ton="pozor">vyprší za {zbyva} {zbyva === 1 ? "den" : zbyva < 5 ? "dny" : "dní"} ({datum(t.platnost_do)})</Stitek>
                          : <Stitek ton="ok">platí do {datum(t.platnost_do)}</Stitek>}
                      {t.zdravotni && <Stitek ton="problem">zdravotní údaje: zapnuto</Stitek>}
                      <Stitek>{kdy(t.naposledy_at)}</Stitek>
                      <Stitek>vydáno {datum(t.vytvoreno_at)}</Stitek>
                      {admin && <Stitek ton="info" ikona={Eye}>čte vše jako owner</Stitek>}
                      {/* Seznam sekcí je dlouhý, na mobilu se musí zalomit (jinak přetekl z karty). */}
                      {admin && <Stitek ton="ok" ikona={Pencil} className="!whitespace-normal !rounded-2xl [&>svg]:shrink-0">zapisuje: {(data?.admin_zapis || []).map((k) => ZAPIS_STITEK[k] || k).join(", ")}</Stitek>}
                    </div>
                  </div>
                </div>
                <div className="flex flex-col gap-2 sm:self-center sm:flex-row">
                  {!admin && <button type="button" className="btn-secondary w-full sm:w-auto" onClick={() => kopirujBrief(t)}><Copy className="h-4 w-4" /> Zkopírovat brief</button>}
                  <button type="button" className="btn-primary w-full sm:w-auto" disabled={!!prace} onClick={() => setSprava(t.id)}>Spravovat token</button>
                </div>
              </div>
            );
          })}
          <div className={`flex flex-col gap-3 rounded-2xl border border-slate-200 p-3 sm:flex-row sm:items-center ${data?.spolecny?.aktivni ? "bg-white" : "bg-slate-50 opacity-70"}`}>
            <div className="flex min-w-0 flex-1 gap-3">
              <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-bold text-white ${ton("neutralni").plny}`}>∗</div>
              <div className="min-w-0 flex-1">
                <div className="font-bold text-slate-900">Společný token (původní asistent)</div>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  <Stitek>jménem ownera</Stitek>
                  {data?.spolecny?.aktivni ? <Stitek ton="ok">funguje</Stitek> : <Stitek>vypnutý</Stitek>}
                  {data?.spolecny?.z_env && <Stitek ton="pozor">nastavený v Netlify</Stitek>}
                </div>
              </div>
            </div>
            {data?.spolecny?.aktivni && !data?.spolecny?.z_env && (
              <button type="button" className="btn-danger btn-sm" disabled={!!prace} onClick={vypnoutSpolecny}>Vypnout</button>
            )}
          </div>
        </div>
        {zrusene.length > 0 && <ZruseneTokeny zrusene={zrusene} />}
      </Card>

      <Card title="Povolené AI nástroje" ikona={ShieldCheck} odznak="token jen pro tyto"
        action={<button type="button" className="btn-secondary btn-sm" onClick={() => setNastrojEdit({})}><Plus className="h-4 w-4" /> Přidat nástroj</button>}>
        <p className="mb-2 text-sm text-slate-600">Jen firemní nebo placená verze se smlouvou o zpracování dat (DPA) a bez trénování na datech. Vyřazený nástroj okamžitě vypne všechny jeho tokeny.</p>
        {nastroje.length === 0 && <p className="text-sm italic text-slate-500">Zatím žádný nástroj.</p>}
        <div className="divide-y divide-slate-100">
          {nastroje.map((n) => (
            <div key={n.id} className={`flex items-center gap-3 py-2.5 ${n.aktivni ? "" : "opacity-60"}`}>
              <div className="min-w-0 flex-1 text-sm">
                <div className="flex flex-wrap items-center gap-1.5"><span className="font-bold text-slate-900">{n.nazev}</span>{!n.aktivni && <Stitek>vyřazený</Stitek>}</div>
                {n.popis && <div className="mt-0.5 text-slate-600">{n.popis}</div>}
              </div>
              <button type="button" className="btn-secondary btn-sm" onClick={() => setNastrojEdit(n)}>Upravit</button>
              {n.aktivni
                ? <button type="button" className="btn-ghost btn-sm" disabled={!!prace} onClick={async () => { if (await confirmDialog(`Vyřadit ${n.nazev}? Všechny tokeny pro tento nástroj okamžitě přestanou platit. Když nástroj později znovu povolíš, jeho nezrušené tokeny zase začnou fungovat.`)) akce(`n${n.id}`, { akce: "nastroj", id: n.id, aktivni: false }, "Nástroj vyřazený"); }}>Vyřadit</button>
                : <button type="button" className="btn-ghost btn-sm" disabled={!!prace} onClick={() => akce(`n${n.id}`, { akce: "nastroj", id: n.id, aktivni: true }, "Nástroj je zase povolený")}>Obnovit</button>}
            </div>
          ))}
        </div>
      </Card>

      {vydani && <VydaniTokenu data={data} onClose={() => { setVydani(false); refetch(); }} />}
      {vydaniAdmin && <VydaniTokenu admin data={data} onClose={() => { setVydaniAdmin(false); refetch(); }} />}
      {spravovany && (
        <SpravaTokenu
          t={spravovany}
          data={data}
          prace={prace}
          onClose={() => setSprava(null)}
          onProdlouzit={() => akce(`p${spravovany.id}`, { akce: "prodlouzit", id: spravovany.id }, `Prodlouženo o ${(spravovany.typ === "admin" ? data?.admin_platnost_dni : data?.platnost_dni) || 90} dní`)}
          onZdravotni={() => zdravotni(spravovany)}
          onZrusit={() => zrusit(spravovany)}
        />
      )}
      {nastrojEdit && <UpravaNastroje nastroj={nastrojEdit} onClose={() => { setNastrojEdit(null); refetch(); }} />}
    </div>
  );
}

// Okno Spravovat token: velká tlačítka, na mobilu spodní list (Modal). Žádná akce se neopakuje.
function SpravaTokenu({ t, data, prace, onClose, onProdlouzit, onZdravotni, onZrusit }) {
  const admin = t.typ === "admin";
  const dni = (admin ? data?.admin_platnost_dni : data?.platnost_dni) || (admin ? 180 : 90);
  const zbyva = dniDo(t.platnost_do);
  const prosly = zbyva <= 0;
  const brzy = !prosly && zbyva <= (admin ? (data?.admin_upozornit_dni || 10) : (data?.upozornit_dni || 14));
  return (
    <Modal open onClose={onClose} size="sm" title={admin ? t.stitek : (t.user_name || t.user_email)}>
      <div className="space-y-3">
        {(brzy || prosly) && (
          <p className={`rounded-xl px-3 py-2 text-sm ${ton(prosly ? "problem" : "pozor").pole}`}>
            {prosly ? `Token vypršel ${datum(t.platnost_do)}, bot nefunguje.` : `Token vyprší za ${dnyText(zbyva)} (${datum(t.platnost_do)}).`}
          </p>
        )}
        <button type="button" className="btn-primary w-full" disabled={!!prace} onClick={onProdlouzit}>
          <RotateCcw className="h-5 w-5" /> {prosly ? `Obnovit prošlý token na ${dni} dní` : `Prodloužit o ${dni} dní`}
        </button>
        <p className="-mt-1 text-xs text-slate-500">Platnost se počítá od dneška, token zůstává stejný.</p>
        <Zapinatko zapnuto={!!t.zdravotni} onZmena={() => { if (!prace) onZdravotni(); }} popisek="Zdravotní údaje klientek" ikona={HeartPulse} />
        <p className="-mt-1 text-xs text-slate-500">Když je zapnuto, bot vidí zdravotní údaje klientek a každé čtení jde do auditu.</p>
        <button type="button" className="btn-danger w-full" disabled={!!prace} onClick={onZrusit}>Zrušit token</button>
      </div>
    </Modal>
  );
}

// Zrušené tokeny: sbalené, po rozbalení plynule doskroluje k obsahu (pravidlo z CLAUDE.md).
function ZruseneTokeny({ zrusene }) {
  const [otevreno, setOtevreno] = useState(false);
  const ref = useRef(null);
  const prepni = () => {
    const nove = !otevreno;
    setOtevreno(nove);
    if (nove) setTimeout(() => ref.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 50);
  };
  return (
    <div className="mt-3">
      <button type="button" className="btn-ghost btn-sm" onClick={prepni} aria-expanded={otevreno}>
        <ChevronDown className={`h-4 w-4 transition-transform ${otevreno ? "" : "-rotate-90"}`} /> Zrušené tokeny <Stitek>{zrusene.length}</Stitek>
      </button>
      {otevreno && (
        <div ref={ref} className="mt-2 scroll-mt-24 space-y-2 text-sm text-slate-600">
          {zrusene.map((t) => (
            <div key={t.id} className="flex flex-wrap items-center gap-1.5">
              <span className="font-semibold text-slate-800">{t.user_name || t.user_email}</span>
              <Stitek>{t.nastroj}</Stitek>
              <Stitek>zrušeno {datum(t.zruseno_at)}</Stitek>
              {t.zruseno_duvod && <Stitek>{t.zruseno_duvod}</Stitek>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function VydaniTokenu({ data, onClose, admin = false }) {
  const lide = data?.uzivatele || [];
  const nastroje = (data?.nastroje || []).filter((n) => n.aktivni);
  const [email, setEmail] = useState("");
  const [nastrojId, setNastrojId] = useState(nastroje[0]?.id || "");
  const [zdravotni, setZdravotni] = useState(false);
  const [token, setToken] = useState("");
  const [prace, setPrace] = useState(false);

  const vydat = async () => {
    if ((!admin && !email) || !nastrojId) { toast(admin ? "Vyber AI nástroj" : "Vyber člověka a AI nástroj", "error"); return; }
    setPrace(true);
    try { const j = await posli(admin ? { akce: "vydat_admin", nastroj_id: nastrojId, zdravotni } : { akce: "vydat", email, nastroj_id: nastrojId, zdravotni }); setToken(j.token); }
    catch (e) { toast(e.message, "error"); }
    finally { setPrace(false); }
  };
  const kopirovat = async () => {
    try { await navigator.clipboard.writeText(token); toast("Token zkopírovaný", "success"); }
    catch { toast("Kopírování se nepovedlo, označ token ručně", "error"); }
  };
  const kopirovatBrief = async () => {
    const u = lide.find((x) => x.email === email) || {};
    try { await navigator.clipboard.writeText(briefProBota({ jmeno: u.name || u.email, role: u.role, zdravotni })); toast("Brief pro bota zkopírovaný", "success"); }
    catch { toast("Kopírování se nepovedlo", "error"); }
  };
  const zavrit = async () => {
    if (token && !await confirmDialog({ title: "Máš token zkopírovaný?", description: "Po zavření už ho znovu neuvidíš.", confirmLabel: "Ano, zavřít", variant: "primary" })) return;
    setToken(""); onClose();
  };

  return (
    <Modal open onClose={zavrit} title={admin ? "Vydat admin token Admin Aneta" : "Vydat token pro bota"}
      footer={token
        ? <><button type="button" className="btn-primary" onClick={kopirovat}><Copy className="h-4 w-4" /> Zkopírovat token</button>{!admin && <button type="button" className="btn-secondary" onClick={kopirovatBrief}><Copy className="h-4 w-4" /> Zkopírovat brief</button>}<button type="button" className="btn-secondary" onClick={zavrit}>Hotovo</button></>
        : <><button type="button" className="btn-secondary" onClick={onClose}><XIcon className="h-4 w-4" /> Zrušit</button><button type="button" className="btn-primary" disabled={prace} onClick={vydat}><KeyRound className="h-4 w-4" /> Vydat token</button></>}>
      {!token ? (
        <div className="space-y-3">
          {!admin && (
            <Field label="Člověk">
              <select className="field w-full" value={email} onChange={(e) => setEmail(e.target.value)}>
                <option value="">Vyber člověka…</option>
                {lide.map((u) => <option key={u.email} value={u.email}>{u.name || u.email} ({ROLE[u.role] || u.role})</option>)}
              </select>
            </Field>
          )}
          <Field label="AI nástroj (jen povolené)">
            <select className="field w-full" value={nastrojId} onChange={(e) => setNastrojId(e.target.value)}>
              {nastroje.map((n) => <option key={n.id} value={n.id}>{n.nazev}</option>)}
            </select>
          </Field>
          <Zapinatko zapnuto={zdravotni} onZmena={setZdravotni} popisek="Zdravotní údaje klientek (každé čtení jde do auditu)" />
          {admin ? (
            <p className="text-sm text-slate-600">Token patří tobě a bot ho používá pod jménem Admin Aneta. Čte vše jako owner, zapisuje jen {(data?.admin_zapis || []).map((k) => ZAPIS_STITEK[k] || k).join(", ")}. Platnost {data?.admin_platnost_dni || 180} dní, {data?.admin_upozornit_dni || 10} dní před koncem ti přijde upozornění a Aneta ti to připomene sama. Smí být jen jeden.</p>
          ) : (
            <p className="text-sm text-slate-600">Platnost {data?.platnost_dni || 90} dní. {data?.upozornit_dni || 14} dní před koncem ti přijde upozornění, prodloužíš jedním klikem.</p>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <div className={`select-all break-all rounded-xl px-3 py-2.5 font-mono text-[13px] font-bold ${ton("znacka").pole}`}>{token}</div>
          <p className="text-sm text-slate-700"><b>Zkopíruj ho teď</b> a vlož do AI nástroje. Znovu se zobrazit nedá, v adminu je jen jeho otisk.</p>
        </div>
      )}
    </Modal>
  );
}

function UpravaNastroje({ nastroj, onClose }) {
  const [nazev, setNazev] = useState(nastroj.nazev || "");
  const [popis, setPopis] = useState(nastroj.popis || "");
  const [prace, setPrace] = useState(false);
  const ulozit = async () => {
    if (!nazev.trim()) { toast("Vyplň název nástroje", "error"); return; }
    setPrace(true);
    try { await posli({ akce: "nastroj", id: nastroj.id || null, nazev: nazev.trim(), popis: popis.trim() }); toast("Uloženo", "success"); onClose(); }
    catch (e) { toast(e.message, "error"); }
    finally { setPrace(false); }
  };
  return (
    <Modal open onClose={onClose} title={nastroj.id ? "Upravit AI nástroj" : "Přidat AI nástroj"}
      footer={<><button type="button" className="btn-secondary" onClick={onClose}>Zrušit</button><button type="button" className="btn-primary" disabled={prace} onClick={ulozit}>Uložit</button></>}>
      <div className="space-y-3">
        <Field label="Název"><input className="field w-full" value={nazev} maxLength={80} onChange={(e) => setNazev(e.target.value)} placeholder="Například Claude Team" /></Field>
        <Field label="Podmínky" hint="Firemní nebo placená verze, smlouva o zpracování dat (DPA), bez trénování na datech."><input className="field w-full" value={popis} onChange={(e) => setPopis(e.target.value)} placeholder="firemní, bez trénování na datech, DPA podepsaná 22. 10. 2026" /></Field>
      </div>
    </Modal>
  );
}
